import { formatNumber } from "util/formatNumber";

/** Whole kilograms for display — converted totals carry noisy decimals. */
export const formatKg = (kg: number) => `${formatNumber(Math.round(kg))} kg`;

/**
 * The subset of `FacilityAccumulationSummary` the on-site cap card's caption
 * and judged-math derivation reads. A structural (not imported) type so this
 * module has no dependency on the generated query shape.
 */
export type OnSiteCapSummaryInput = {
  nonAcuteOnSiteKg: number;
  acuteOnSiteKg: number;
  onSiteCapKg?: number | null;
  acuteOnSiteCapKg?: number | null;
  onSiteCapAppliesTo?: string | null;
  episodicExemptNonAcuteKg: number;
  episodicExemptAcuteKg: number;
  capGeneratorStatus?: string | null;
};

export type OnSiteCapDisplay = {
  /** The card's headline caption: the plain "on-site total" variants, or the
   * judged-math sentence once an episodic exemption applies. */
  caption: string;
  // Only present when governing rules impose a cap on acute waste specifically
  acuteCaption?: string;
  acuteOver: boolean;
  /** Exemption sub-line, present only when exempt mass is nonzero. */
  exemptCaption?: string;
};

/**
 * Derives the on-site cap stat card's caption and sub-caption text, folding
 * in the episodic exemption once an active event carves mass out of the cap
 * judgment. Zero exempt mass (the common case: no active event, or none of
 * its containers count against the cap) reproduces today's plain totals so
 * the card doesn't change for facilities with no episodic activity.
 */
export function deriveOnSiteCapDisplay(
  summary: OnSiteCapSummaryInput | undefined
): OnSiteCapDisplay {
  const totalOnSiteKg = (summary?.nonAcuteOnSiteKg ?? 0) + (summary?.acuteOnSiteKg ?? 0);
  const exemptKg = (summary?.episodicExemptNonAcuteKg ?? 0) + (summary?.episodicExemptAcuteKg ?? 0);

  // Judged net of exempt acute mass, mirroring the backend's
  // `capJudgedAcuteKg`: an episodic event's exempt acute container is still
  // physically on site (counted in `acuteOnSiteKg` above) but doesn't count
  // against the 1 kg acute cap, so the sub-line must agree with the
  // over/not-over verdict the summary's `overCap` already reflects.
  const acuteExemptKg = summary?.episodicExemptAcuteKg ?? 0;
  const judgedAcuteKg = (summary?.acuteOnSiteKg ?? 0) - acuteExemptKg;
  const acuteOver = summary?.acuteOnSiteCapKg != null && judgedAcuteKg > summary.acuteOnSiteCapKg;
  const acuteCaption =
    summary?.acuteOnSiteCapKg != null
      ? acuteCaptionText(judgedAcuteKg, summary.acuteOnSiteCapKg, acuteExemptKg)
      : undefined;
  const exemptCaption =
    summary?.onSiteCapKg != null && exemptKg > 0
      ? `${formatKg(exemptKg)} exempt — excluded from the cap judgment`
      : undefined;

  return {
    caption: capCaption(summary, totalOnSiteKg, exemptKg),
    acuteCaption,
    acuteOver,
    exemptCaption,
  };
}

function capCaption(
  summary: OnSiteCapSummaryInput | undefined,
  totalOnSiteKg: number,
  exemptKg: number
): string {
  if (summary == null) {
    return "On-site total";
  }
  if (summary.onSiteCapKg == null) {
    return noCapCaption(summary);
  }
  if (exemptKg <= 0) {
    return `On-site total - ${formatKg(summary.onSiteCapKg)} cap`;
  }
  return judgedCapCaption(summary, summary.onSiteCapKg, totalOnSiteKg, exemptKg);
}

/**
 * The acute sub-line's text: the judged mass against the 1 kg cap, plus a
 * parenthetical noting any exempt acute mass — with the same "excluded from
 * the cap judgment" wording the headline's `exemptCaption` uses, not language
 * suggesting the cap itself moved.
 */
function acuteCaptionText(
  judgedAcuteKg: number,
  acuteOnSiteCapKg: number,
  exemptKg: number
): string {
  const base = `Acute: ${formatNumber(judgedAcuteKg)} kg of ${formatNumber(acuteOnSiteCapKg)} kg cap`;
  if (exemptKg <= 0) {
    return base;
  }
  return `${base} (${formatNumber(exemptKg)} kg exempt — excluded from the cap judgment)`;
}

function noCapCaption(summary: OnSiteCapSummaryInput): string {
  if (summary.capGeneratorStatus === "LARGE_QUANTITY_GENERATOR") {
    return "On-site total - no cap (LQG)";
  }
  return "On-site total - no cap set";
}

/**
 * The judged-math caption once exempt mass is present. VSQG's cap governs
 * non-acute mass alone (`onSiteCapAppliesTo === "NON_ACUTE"`), so its judged
 * number excludes acute mass entirely — mirroring how the card already
 * splits acute out into its own sub-caption against its own 1 kg cap. SQG's
 * cap governs the combined total.
 */
function judgedCapCaption(
  summary: OnSiteCapSummaryInput,
  onSiteCapKg: number,
  totalOnSiteKg: number,
  exemptKg: number
): string {
  if (summary.onSiteCapAppliesTo === "NON_ACUTE") {
    const judgedNonAcuteKg = summary.nonAcuteOnSiteKg - summary.episodicExemptNonAcuteKg;
    return `${formatKg(judgedNonAcuteKg)} counted against ${formatKg(
      onSiteCapKg
    )} cap — cap applies to non-acute waste`;
  }
  const judgedKg = totalOnSiteKg - exemptKg;
  return `${formatKg(judgedKg)} counted against ${formatKg(onSiteCapKg)} cap`;
}
